import { useState } from 'react';
import AppIcon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useToast } from '../../../hooks';

export default function SecurityTab() {
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [isChanging, setIsChanging] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const calculatePasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '' };
    
    let score = 0;
    
    // Length check
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    
    // Character variety
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    
    if (score <= 2) return { score, label: 'Weak', color: 'bg-red-500' };
    if (score <= 4) return { score, label: 'Fair', color: 'bg-yellow-500' };
    if (score <= 5) return { score, label: 'Good', color: 'bg-blue-500' };
    return { score, label: 'Strong', color: 'bg-green-500' };
  };

  const validatePassword = () => {
    const errors = [];
    
    if (!formData.currentPassword) {
      errors.push('Current password is required');
    }
    
    if (!formData.newPassword) {
      errors.push('New password is required');
    } else {
      if (formData.newPassword.length < 8) {
        errors.push('Password must be at least 8 characters');
      }
      if (!/[a-z]/.test(formData.newPassword)) {
        errors.push('Password must contain at least one lowercase letter');
      }
      if (!/[A-Z]/.test(formData.newPassword)) {
        errors.push('Password must contain at least one uppercase letter');
      }
      if (!/[0-9]/.test(formData.newPassword)) {
        errors.push('Password must contain at least one number');
      }
      if (!/[^A-Za-z0-9]/.test(formData.newPassword)) {
        errors.push('Password must contain at least one special character');
      }
    }
    
    if (formData.newPassword === formData.currentPassword) {
      errors.push('New password must be different from current password');
    }
    
    if (formData.newPassword !== formData.confirmPassword) {
      errors.push('Passwords do not match');
    }
    
    return errors;
  };

  const handleChangePassword = async () => {
    const errors = validatePassword();
    
    if (errors.length > 0) {
      showToast(errors[0], 'error');
      return;
    }
    
    setIsChanging(true);
    try {
      // TODO: API call to change password
      // await changePassword(formData.currentPassword, formData.newPassword);
      
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));
      
      showToast('Password changed successfully', 'success');
      
      // Reset form
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setShowPasswords({
        current: false,
        new: false,
        confirm: false,
      });
      
      // Optional: Logout user after password change
      // setTimeout(() => {
      //   dispatch(logout());
      // }, 2000);
    } catch (error) {
      showToast('Failed to change password. Please check your current password.', 'error');
      console.error('Error changing password:', error);
    } finally {
      setIsChanging(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setShowPasswords({
      current: false,
      new: false,
      confirm: false,
    });
  };

  const passwordStrength = calculatePasswordStrength(formData.newPassword);
  const hasChanges = formData.currentPassword || formData.newPassword || formData.confirmPassword;
  const isValid = validatePassword().length === 0;

  return (
    <div className="max-w-2xl space-y-8">
      {/* Change Password Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Change Password</h3>
        <p className="text-sm text-gray-500 mb-6">
          Ensure your account is using a long, random password to stay secure.
        </p>

        <div className="space-y-6">
          {/* Current Password */}
          <div>
            <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
              Current Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Input
                id="currentPassword"
                name="currentPassword"
                type={showPasswords.current ? 'text' : 'password'}
                value={formData.currentPassword}
                onChange={handleInputChange}
                placeholder="Enter current password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <AppIcon
                  icon={showPasswords.current ? 'EyeOff' : 'Eye'}
                  className="w-5 h-5"
                />
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
              New Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Input
                id="newPassword"
                name="newPassword"
                type={showPasswords.new ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={handleInputChange}
                placeholder="Enter new password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <AppIcon
                  icon={showPasswords.new ? 'EyeOff' : 'Eye'}
                  className="w-5 h-5"
                />
              </button>
            </div>
            
            {/* Password Strength Indicator */}
            {formData.newPassword && (
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                      style={{ width: `${(passwordStrength.score / 6) * 100}%` }}
                    />
                  </div>
                  <span className={`text-sm font-medium ${
                    passwordStrength.score <= 2 ? 'text-red-600' :
                    passwordStrength.score <= 4 ? 'text-yellow-600' :
                    passwordStrength.score <= 5 ? 'text-blue-600' :
                    'text-green-600'
                  }`}>
                    {passwordStrength.label}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showPasswords.confirm ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm new password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <AppIcon
                  icon={showPasswords.confirm ? 'EyeOff' : 'Eye'}
                  className="w-5 h-5"
                />
              </button>
            </div>
            
            {/* Match Indicator */}
            {formData.confirmPassword && (
              <div className="mt-2 flex items-center gap-2">
                {formData.newPassword === formData.confirmPassword ? (
                  <>
                    <AppIcon name="CheckCircle" className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-600">Passwords match</span>
                  </>
                ) : (
                  <>
                    <AppIcon name="XCircle" className="w-4 h-4 text-red-600" />
                    <span className="text-sm text-red-600">Passwords do not match</span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Password Requirements */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AppIcon name="Info" className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-blue-900 mb-2">
                  Password Requirements
                </h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li className="flex items-center gap-2">
                    <AppIcon
                      icon={formData.newPassword.length >= 8 ? 'Check' : 'Circle'}
                      className={`w-3 h-3 ${formData.newPassword.length >= 8 ? 'text-green-600' : 'text-blue-400'}`}
                    />
                    At least 8 characters
                  </li>
                  <li className="flex items-center gap-2">
                    <AppIcon
                      icon={/[A-Z]/.test(formData.newPassword) ? 'Check' : 'Circle'}
                      className={`w-3 h-3 ${/[A-Z]/.test(formData.newPassword) ? 'text-green-600' : 'text-blue-400'}`}
                    />
                    One uppercase letter
                  </li>
                  <li className="flex items-center gap-2">
                    <AppIcon
                      icon={/[a-z]/.test(formData.newPassword) ? 'Check' : 'Circle'}
                      className={`w-3 h-3 ${/[a-z]/.test(formData.newPassword) ? 'text-green-600' : 'text-blue-400'}`}
                    />
                    One lowercase letter
                  </li>
                  <li className="flex items-center gap-2">
                    <AppIcon
                      icon={/[0-9]/.test(formData.newPassword) ? 'Check' : 'Circle'}
                      className={`w-3 h-3 ${/[0-9]/.test(formData.newPassword) ? 'text-green-600' : 'text-blue-400'}`}
                    />
                    One number
                  </li>
                  <li className="flex items-center gap-2">
                    <AppIcon
                      icon={/[^A-Za-z0-9]/.test(formData.newPassword) ? 'Check' : 'Circle'}
                      className={`w-3 h-3 ${/[^A-Za-z0-9]/.test(formData.newPassword) ? 'text-green-600' : 'text-blue-400'}`}
                    />
                    One special character
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
        <Button
          variant="ghost"
          onClick={handleCancel}
          disabled={!hasChanges || isChanging}
        >
          Cancel
        </Button>
        <Button
          onClick={handleChangePassword}
          disabled={!hasChanges || !isValid || isChanging}
          className="gap-2"
        >
          {isChanging ? (
            <>
              <AppIcon name="Loader2" className="w-4 h-4 animate-spin" />
              Changing Password...
            </>
          ) : (
            <>
              <AppIcon name="Lock" className="w-4 h-4" />
              Change Password
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
